import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Banknote, Users, Zap } from "lucide-react";
import { useBillCycleLastRunDetails } from "../../hooks";
import { useParams } from "react-router-dom";
import { Progress } from "@shared/ui/progress";

function LastRunDetails() {
  const { id } = useParams();
  const { data: lastRunDetails } = useBillCycleLastRunDetails(id);

  if (!lastRunDetails) {
  return (
    <Card className="w-full">
      <CardContent className="flex items-center justify-center py-10">
        <p className="text-sm text-muted-foreground text-center">
          Run Details not found
        </p>
      </CardContent>
    </Card>
  );
}
  const quickStatsData = [
    {
      title: "Total Bills",
      count: lastRunDetails?.kpiCards?.totalBills,
    },
    {
      title: "Actual",
      count: lastRunDetails?.kpiCards?.actualBills,
    },
    {
      title: "Estimated",
      count: lastRunDetails?.kpiCards?.estimatedBills,
    },
    {
      title: "Failed",
      count: lastRunDetails?.kpiCards?.failedBills,
    },
    {
      title: "Excluded",
      count: lastRunDetails?.kpiCards?.excludedBills,
    },
  ];
  return (
    <div className="mt-4">
      <div className="space-y-6">
        {/* Header with Title and Billing Period Dropdown */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Last Run Details
          </h3>
          {/* <div className="w-[280px] space-y-1.5">
                  <label className="text-sm font-medium text-gray-700">Billing Period</label>
                  <Select value={billingPeriod} onValueChange={setBillingPeriod}>
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="Select Billing Period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="apr-2025">1 April 2025 - 30 April 2025</SelectItem>
                      <SelectItem value="mar-2025">1 March 2025 - 31 March 2025</SelectItem>
                      <SelectItem value="feb-2025">1 February 2025 - 28 February 2025</SelectItem>
                    </SelectContent>
                  </Select>
                </div> */}
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {quickStatsData.map(({ title, count }) => (
            <QuickStatsCard key={title} title={title} count={count} />
          ))}
        </div>

        {/* Bills Breakdown by Category */}
        <Card className="border-gray-200 shadow-md">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-gray-800" />
              <span className="text-gray-900">Bills Breakdown by Category</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              {lastRunDetails?.billsBreakdownByCategory?.map(
                ({ category, count, percentage }) => (
                  <div key={category} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700">
                        {category}
                      </span>

                      <span className="text-gray-600 font-semibold">
                        {count} ({percentage}%)
                      </span>
                    </div>

                    <Progress value={percentage} className="h-2.5" />
                  </div>
                )
              )}
            </div>
          </CardContent>
        </Card>

        {/* Financial and Consumption Summaries */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Financial Summary */}
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Banknote className="w-5 h-5 text-blue-900" />
                <span className="text-blue-900">Financial Summary</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1 p-3 bg-white rounded-lg border border-blue-100">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">
                    Billed Amount
                  </span>
                  <p className="card-value-text text-gray-900">{lastRunDetails?.financialSummary?.billedAmount}</p>
                </div>
                <div className="space-y-1 p-3 bg-white rounded-lg border border-blue-100">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">
                    Avg Bill
                  </span>
                  <p className="card-value-text text-blue-600">{lastRunDetails?.financialSummary?.avgBill}</p>
                </div>
                <div className="space-y-1 p-3 bg-white rounded-lg border border-orange-100">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">
                    Outstanding
                  </span>
                  <p className="card-value-text text-orange-600">{lastRunDetails?.financialSummary?.outstanding}</p>
                </div>
                <div className="space-y-1 p-3 bg-white rounded-lg border border-red-100">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">
                    Late Fees
                  </span>
                  <p className="card-value-text text-red-600">{lastRunDetails?.financialSummary?.lateFees}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Consumption Summary */}
          <Card className="border-green-200 bg-gradient-to-br from-green-50 to-white shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="w-5 h-5 text-green-900" />
                <span className="text-green-900">Consumption Summary</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1 p-3 bg-white rounded-lg border border-green-100">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">
                    Total Consumption
                  </span>
                  <p className="card-value-text text-gray-900">{lastRunDetails?.consumptionSummary?.totalConsumption}</p>
                </div>
                <div className="space-y-1 p-3 bg-white rounded-lg border border-blue-100">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">
                    Avg Consumption
                  </span>
                  <p className="card-value-text text-blue-600">{lastRunDetails?.consumptionSummary?.avgConsumption}</p>
                </div>
                <div className="space-y-1 p-3 bg-white rounded-lg border border-green-100">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">
                    High Consumption
                  </span>
                  <p className="card-value-text text-green-600">{lastRunDetails?.consumptionSummary?.highConsumption}</p>
                </div>
                <div className="space-y-1 p-3 bg-white rounded-lg border border-purple-100">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">
                    Low Consumption
                  </span>
                  <p className="card-value-text text-purple-600">{lastRunDetails?.consumptionSummary?.lowConsumption}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default LastRunDetails;
